import { Navigate, Outlet } from "react-router-dom";

import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import { useModules } from "@/lib/modules";
import { MODULE_GROUP_DEFENDER } from "@/lib/api/modules";
import { useTenantPathFn } from "@/lib/tenant";

export interface RequireModuleProps {
  /** The module id these routes belong to. Omit when guarding a group. */
  module?: string;
  /**
   * A module GROUP these routes belong to: the routes render while ANY module
   * in the group is in effect. Used for Defender, where one section covers five
   * separately licensed products and disappears only when all five are out.
   */
  group?: string;
  /** Where to send somebody who arrived at a disabled module's URL. */
  fallback?: string;
}

/**
 * A route guard for an optional module.
 *
 * The navigation already omits a disabled module's entries, but a URL is not
 * navigation: a bookmark, a link in a ticket, or the browser's history will all
 * still reach the path. Without this the operator would land on a page whose
 * every request answers 409 and read it as a broken console rather than as one
 * configured this way.
 *
 * It redirects rather than rendering an explanation. There is nothing to
 * explain at that URL — the deployment does not have this part — and an
 * explanation page would be a fourth thing to maintain for a state the settings
 * screen already describes.
 *
 * While the module list is loading the routes render normally. The server
 * refuses independently, so the worst case is a page that briefly shows its own
 * loading state and then redirects, rather than a console that appears to lose
 * half its function on every slow request.
 */
export function RequireModule({ module, group, fallback = "/overview" }: RequireModuleProps) {
  const { enabled, isPending, defender } = useModules();
  // The fallback is a path inside THIS tenant, not the deployment root.
  const resolve = useTenantPathFn();

  if (isPending) {
    return <LoadingSkeleton variant="page" label="Checking which modules are enabled" />;
  }
  if (module !== undefined && !enabled(module)) {
    return <Navigate to={resolve(fallback)} replace />;
  }
  if (group === MODULE_GROUP_DEFENDER && !defender) {
    return <Navigate to={resolve(fallback)} replace />;
  }
  return <Outlet />;
}
